import React, { useState } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { Link, useNavigate } from 'react-router-dom';
import './SignupPage.css';

function generateCoachCode() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // No O/0, I/1, L
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

async function getUniqueCoachCode() {
  let code = generateCoachCode();
  let attempts = 0;

  while (attempts < 10) {
    const q = query(collection(db, 'coaches'), where('coachCode', '==', code));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return code;
    }
    code = generateCoachCode();
    attempts++;
  }

  // Fallback: add timestamp suffix
  return code + Date.now().toString(36).slice(-2).toUpperCase();
}

function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }

    setLoading(true);

    try {
      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // Generate unique coach code
      const coachCode = await getUniqueCoachCode();

      // Create coach document in Firestore
      await setDoc(doc(db, 'coaches', uid), {
        name: name.trim(),
        brandName: brandName.trim() || '',
        email: email.trim(),
        coachCode: coachCode,
        createdAt: new Date().toISOString(),
        photoURL: ''
      });

      navigate('/dashboard');
    } catch (err) {
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Use at least 8 characters.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        default:
          setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-logo">
          <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="8" y="29" width="26" height="4" rx="2" fill="#2ECC71"/>
            <rect x="66" y="29" width="26" height="4" rx="2" fill="#2ECC71"/>
            <rect x="8" y="48" width="26" height="5" rx="2.5" fill="#2ECC71"/>
            <rect x="66" y="48" width="26" height="5" rx="2.5" fill="#2ECC71"/>
            <rect x="8" y="68" width="26" height="4" rx="2" fill="#2ECC71"/>
            <rect x="66" y="68" width="26" height="4" rx="2" fill="#2ECC71"/>
            <circle cx="50" cy="50" r="17" fill="none" stroke="#ffffff" strokeWidth="3.5"/>
          </svg>
        </div>
        <h1 className="auth-title">Create Account</h1>
        <p className="auth-subtitle">Set up your Coach Connect portal</p>

        <form onSubmit={handleSignup} className="auth-form">
          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Your Name *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="brandName">Academy / Brand Name</label>
            <input
              id="brandName"
              type="text"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="e.g., Your Academy Name (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password *</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-links">
          <span>Already have an account? </span>
          <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
